import find from "lodash/find"
import map from "lodash/map"
import { makeStyles } from "tss-react/mui"
import { Highlight, useHits } from "react-instantsearch"
import getContentUrl from "utils-lib/getContentUrl"
import { getWindow } from "utils-lib/getWindow"
import { Link } from "component-lib/Base"
import { useRef } from "react"
import { pushQueryResultData } from "./analytics/analytics"

const ssrWindow = getWindow()

const isSupportPage = ssrWindow.location.href.indexOf("support") >= 0

const useStyles = makeStyles()((_theme, { isHeader, hideDescription }) => ({
  heading: {
    color: `#64696F`,
  },
  results: {
    "& .ais-Highlight, .ais-Highlight-nonHighlighted": {
      fontSize: `inherit !important`,
    },
    "& .ais-Highlight-nonHighlighted": {
      fontSize: `inherit`,
    },
    "& .ais-Highlight-highlighted": {
      background: `transparent`,
      color: `#005D02`,
      fontStyle: `normal`,
      fontSize: `inherit`,
      paddingTop: 0,
      paddingRight: 0,
      paddingLeft: 0,
      marginRight: 0,
      marginLeft: 0,
    },
  },
  resultContainer: {
    minWidth: 0,
  },
  imageContainer: {
    background: `#fff`,
    border: `1px solid #efefef`,
    borderRadius: 5,
    width: hideDescription ? 42 : 62,
    height: hideDescription ? 42 : 62,
  },
  content: {
    minWidth: 0,
  },
  result: {
    "&:hover,&.focused": isHeader
      ? {
          background: `#EAF3E8`,
        }
      : {
          "& .Suggestion__title": {
            color: `#1d890a`,
            textDecoration: "underline",
          },
        },
    "&.focused .Suggestion__title": {
      textDecoration: "underline",
    },
    "& .btn": {
      borderRadius: 0,
    },
  },
}))

const Suggestion = ({
  isHeader,
  hit,
  sendEvent,
  className = ``,
  filtersToRename = () => {},
  hideDescription = false,
  smallDescription = false,
}) => {
  if (!hit) {
    return null
  }

  const { classes } = useStyles({
    isHeader,
    hideDescription,
  })

  const getAnalyticsData = () => {
    pushQueryResultData(hit, isSupportPage)
  }

  const resultRef = useRef(null)

  const keywordsArr =
    hit.keywords && hit.keywords !== `undefined`
      ? hit.keywords.split(",").slice(0, 3)
      : []

  return (
    <>
      <div className={classes.results}>
        <div className={`${classes.result} ${className}`} ref={resultRef}>
          <div className="mx-3" data-testid="Suggestion">
            <Link
              href={hit.path}
              isExternalLink
              analytics_key={
                isSupportPage
                  ? `support-search-query-result-selection`
                  : `global-search-query-result-selection`
              }
              className="w-100 a-taggable wm-grey-light wm-border-bottom d-flex align-items-center wm-cursor-pointer py-2"
              onClick={() => {
                sendEvent("click", hit, "Suggestion Clicked")
                getAnalyticsData()
              }}
              data-testid="SuggestionLink"
              onFocus={() => {
                resultRef.current.classList.add("focused")
              }}
              onBlur={() => {
                resultRef.current.classList.remove("focused")
              }}
            >
              <div
                className={`px-3 d-flex align-items-center ${classes.resultContainer}`}
              >
                <div
                  className={`flex-shrink-0 mr-3 p-1 ${classes.imageContainer}`}
                >
                  <img
                    className="w-100 h-100"
                    src={getContentUrl(hit.image)}
                    alt=""
                  />
                </div>
                <div className={`${classes.content}`}>
                  <div
                    className="maax-font-weight-bold show-ellipses"
                    data-testid="SuggestionTitle"
                  >
                    <Highlight
                      className="Suggestion__title"
                      attribute="title"
                      hit={hit}
                    />
                  </div>
                  {!hideDescription &&
                    hit.description &&
                    hit.description !== "null" && (
                      <div
                        className={`mt-1 mb-1 show-ellipses ${
                          smallDescription ? `font-sm` : ``
                        }`}
                        data-testid="ResultDescription"
                      >
                        <Highlight attribute="description" hit={hit} />
                      </div>
                    )}
                  {!!keywordsArr.length && (
                    <div className="font-xs wm-link show-ellipses">
                      in{" "}
                      {map(keywordsArr, (item1) => {
                        const renamed = find(
                          filtersToRename,
                          () =>
                            hit.originalName.toLowerCase() ===
                            item1.trim().toLowerCase(),
                        )

                        const name = renamed ? renamed.newName : item1.trim()

                        return <span>{name}, </span>
                      })}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// Suggestion.propTypes = {
//   isHeader: PropTypes.bool.isRequired,
//   hit: PropTypes.object.isRequired,
//   sendEvent: PropTypes.func.isRequired,
//   className: PropTypes.string,
//   filtersToRename: PropTypes.func,
//   hideDescription: PropTypes.bool,
//   smallDescription: PropTypes.bool,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// Suggestion.defaultProps = {
//   className: ``,
//   filtersToRename: () => {},
//   smallDescription: false,
//   hideDescription: false,
// }

const Suggestions = (props) => {
  const propsWithDefaults = {
    ...props,
    className: props.className || ``,
    smallDescription: props.smallDescription || true,
    hideDescription: props.hideDescription || true,
    isHeader: props.isHeader || true,
  }
  const { indexName, className, smallDescription, hideDescription, isHeader } =
    propsWithDefaults
  const { hits, sendEvent } = useHits(propsWithDefaults)

  return hits.map((hit, index) => (
    <Suggestion
      key={`${indexName}-${index}`}
      className={className}
      hideDescription={hideDescription}
      smallDescription={smallDescription}
      isHeader={isHeader}
      hit={hit}
      sendEvent={sendEvent}
    />
  ))
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// Suggestions.propTypes = {
//   indexName: PropTypes.string.isRequired,
//   className: PropTypes.string,
//   hideDescription: PropTypes.bool,
//   smallDescription: PropTypes.bool,
//   isHeader: PropTypes.bool,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// Suggestions.defaultProps = {
// className: ``,
// smallDescription: true,
// hideDescription: true,
// isHeader: true,
// }

export { Suggestion }

export default Suggestions
